import { DateTimeValueSchemas } from "@validereinc/utilities";
import { z } from "zod";
import {
  createCreateOneRequestSchema,
  createGetListRequestSchema,
  createUpdateManyRequestSchema,
  createUpdateOneRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
  IdentifierSchema,
} from "../util/index.ts";
import { AssetTypeSchema } from "./AssetSchemas.ts";
import { CustomAttributeRecordSchema } from "./CustomAttributeSchemas.ts";
import {
  EstimationMethodSchema,
  NetworkMeasurementType,
} from "./EstimationMethodSchemas.ts";
import { ReportingGroupSchema } from "./ReportingGroupSchemas.ts";

export const AssetGroupStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;
export type AssetGroupStatusType =
  (typeof AssetGroupStatus)[keyof typeof AssetGroupStatus];

export const AssetGroupSchema = z
  .object({
    name: z.string(),
    status: z.nativeEnum(AssetGroupStatus),
    measurement_type: z.nativeEnum(NetworkMeasurementType).optional(),
    default_network_reporting_group_id: z.string().optional(),
    default_network_reporting_group: ReportingGroupSchema.optional(),
    default_estimation_method_id: z.string().optional(),
    default_estimation_method: EstimationMethodSchema.optional(),
    custom_attributes: CustomAttributeRecordSchema,
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe(
    "Represents an asset group in the system, which can include various assets and configurations."
  );
export type AssetGroupType = z.infer<typeof AssetGroupSchema>;

export const AssetGroupAssetSchema = z
  .object({
    name: z.string().optional(),
    asset_type: AssetTypeSchema,
    default_network_adjustable: z.boolean().optional(),
  })
  .merge(DomainModelSchema)
  .describe("Represents an asset under an asset group in the Node API");
export type AssetGroupAssetType = z.infer<typeof AssetGroupAssetSchema>;

export const AssetGroupWithAssetsSchema = AssetGroupSchema.merge(
  z.object({
    assets: z.array(AssetGroupAssetSchema),
    network_inlet_ids: z.array(z.string()),
  })
).describe("Represents an asset group and its assets in the Node API");
export type AssetGroupWithAssetsType = z.infer<
  typeof AssetGroupWithAssetsSchema
>;

export const AssetGroupGetListRequestSchema = createGetListRequestSchema(
  AssetGroupSchema.pick({
    name: true,
    status: true,
    custom_attributes: true,
    created_at: true,
    updated_at: true,
    created_by: true,
    updated_by: true,
  })
    .merge(
      z.object({
        ["asset.id"]: IdentifierSchema,
        id: z.string().uuid().or(z.array(z.string().uuid())),
      })
    )
    .partial(),
  z
    .object({
      period: DateTimeValueSchemas.dateYearMonth,
    })
    .partial()
);
export type AssetGroupGetListRequestType = z.infer<
  typeof AssetGroupGetListRequestSchema
>;

export const AssetGroupCreateOneRequestSchema = createCreateOneRequestSchema(
  AssetGroupSchema.pick({
    name: true,
    status: true,
  })
    .merge(AssetGroupSchema.pick({ custom_attributes: true }).partial())
    .merge(AssetGroupSchema.pick({ measurement_type: true }).required())
    .merge(
      z.object({
        flow_ids: z.array(IdentifierSchema).optional(),
        reporting_group_id: z.string().optional(),
      })
    )
);
export type AssetGroupCreateOneRequestType = z.infer<
  typeof AssetGroupCreateOneRequestSchema
>;

export const AssetGroupUpdateOneRequestSchema = createUpdateOneRequestSchema(
  AssetGroupSchema.pick({
    name: true,
    status: true,
    custom_attributes: true,
  })
    .merge(
      z.object({
        flow_ids: z.array(IdentifierSchema),
        reporting_group_id: z.string(),
        start_date: DateTimeValueSchemas.dateTime,
        end_date: DateTimeValueSchemas.dateTime,
      })
    )
    .partial()
);
export type AssetGroupUpdateOneRequestType = z.infer<
  typeof AssetGroupUpdateOneRequestSchema
>;

export const AssetGroupAssetUpdateManyRequestSchema =
  createUpdateManyRequestSchema(
    z.object({}),
    z.object({
      assetGroupId: IdentifierSchema,
    })
  );
export type AssetGroupAssetUpdateManyRequestType = z.infer<
  typeof AssetGroupAssetUpdateManyRequestSchema
>;
